import React, { useCallback, useMemo, useRef } from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';
import {
  clampZoomRect,
  getZoomRectAt,
  resizeZoomRectFromCorner,
  upsertZoomKeyframe,
  type ZoomKeyframe,
  type ZoomRect,
  type ZoomRegionCorner,
} from '../items/animation/zoom-keyframes';
import { calculateAnimationsValues } from '../items/animation/calculate-animation';
import type { CanHaveZoomKeyframes } from '../items/shared';
import { EditorStarterItem } from '../items/item-type';
import { changeItem } from '../state/actions/change-item';
import { useCanvasTransformationScale } from '../utils/canvas-transformation-context';
import { getCanCrop } from '../utils/get-crop-from-item';
import { getRectAfterCrop } from '../utils/get-dimensions-after-crop';
import { isLeftClick } from '../utils/is-left-click';
import { useFullState, useWriteContext } from '../utils/use-context';

const HANDLE_SIZE = 10; // 화면 px — /scale 로 캔버스 줌 보정

const CORNERS: ZoomRegionCorner[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];

const getItemZoomKeyframes = (item: EditorStarterItem): ZoomKeyframe[] => {
  const zoomKeyframes = (item as EditorStarterItem & CanHaveZoomKeyframes)
    .zoomKeyframes;
  return Array.isArray(zoomKeyframes) ? zoomKeyframes : [];
};

/**
 * 줌 영역 편집 오버레이 — itemSelectedForZoomRegion 대상 아이템의 보이는 창 위에
 * 현재 플레이헤드의 보간 rect 를 그리고, 이동·코너 리사이즈(비율 고정) 드래그를
 * 그 시점의 줌 키프레임 upsert 로 기록한다(zoom-keyframes.ts).
 *
 * VideoMaskOverlay 패턴: 외부 dimming(clip-path) + 테두리 + 핸들. 좌표는 전부
 * composition px, 포인터 델타는 ÷ useCanvasTransformationScale().
 *
 * 애니(이동·확대) 표시 보정은 SelectionOutline 과 같은 규약 — 컨테이너를 애니
 * 오프셋만큼 옮기고 scale 로 감싸 **표시·히트테스트만** 움직인 콘텐츠를 따라간다.
 * 편집 수학(fraction 환산)은 저장 좌표 기준이라 커밋에 이중 반영되지 않는다.
 *
 * ponytail: 회전(rotation) 아이템은 오버레이를 축 정렬로 그린다 — 회전 줌 편집이
 * 필요해지면 SelectionOutline 의 회전 프레임 합성 방식으로 업그레이드.
 */
export const ZoomRegionOverlay: React.FC = () => {
  const state = useFullState();
  const scale = useCanvasTransformationScale();
  const { setState } = useWriteContext();
  const { fps } = useVideoConfig();
  const globalFrame = useCurrentFrame();

  const itemId = state.itemSelectedForZoomRegion;
  const item = itemId ? state.undoableState.items[itemId] : undefined;

  // crop 모드가 같은 아이템을 잡으면 양보, 선택이 풀리면(다른 아이템 선택 포함) 숨김
  const hidden =
    !item ||
    !itemId ||
    !getCanCrop(item) ||
    state.itemSelectedForCrop === itemId ||
    !state.selectedItems.includes(itemId) ||
    item.isDraggingInTimeline;

  const localFrame = item ? Math.round(globalFrame - item.from) : 0;
  const isInRange =
    !!item && localFrame >= 0 && localFrame < item.durationInFrames;

  const keyframes = useMemo(
    () => (item ? getItemZoomKeyframes(item) : []),
    [item],
  );

  const rect = useMemo(
    () => getZoomRectAt(keyframes, localFrame),
    [keyframes, localFrame],
  );

  const visibleBox = useMemo(
    () => (item ? getRectAfterCrop(item) : null),
    [item],
  );

  // 애니가 적용된 현재 프레임의 표시 변형 — selection-outline.tsx 의 animationDisplay
  // 와 동일한 계산. 표시 전용.
  const animationDisplay = useMemo(() => {
    if (!item || !visibleBox) return null;
    const animations =
      'animations' in item &&
      Array.isArray(item.animations) &&
      item.animations.length > 0
        ? item.animations
        : null;
    const positionKeyframes =
      'positionKeyframes' in item &&
      Array.isArray(item.positionKeyframes) &&
      item.positionKeyframes.length > 0
        ? item.positionKeyframes
        : undefined;
    if (!animations && !positionKeyframes) return null;

    const values = calculateAnimationsValues({
      animations: animations ?? [],
      currentFrame: localFrame,
      framesPerSecond: fps,
      totalDurationInFrames: Math.max(1, item.durationInFrames),
      positionKeyframes,
    });

    const offsetX =
      (values.translateX / 100) * visibleBox.width + values.translateXPx;
    const offsetY =
      (values.translateY / 100) * visibleBox.height + values.translateYPx;
    if (offsetX === 0 && offsetY === 0 && values.scale === 1) return null;
    return { offsetX, offsetY, scale: values.scale };
  }, [item, visibleBox, localFrame, fps]);
  const animOffsetX = animationDisplay?.offsetX ?? 0;
  const animOffsetY = animationDisplay?.offsetY ?? 0;
  const animScale = animationDisplay?.scale ?? 1;

  const dragStateRef = useRef<{
    onPointerMove: ((e: PointerEvent) => void) | null;
    onPointerUp: (() => void) | null;
  }>({ onPointerMove: null, onPointerUp: null });

  const startDrag = useCallback(
    (e: React.PointerEvent, corner: ZoomRegionCorner | 'move') => {
      if (!isLeftClick(e) || !itemId || !visibleBox || !isInRange) {
        return;
      }
      e.stopPropagation();
      e.preventDefault();

      if (dragStateRef.current.onPointerMove) {
        window.removeEventListener(
          'pointermove',
          dragStateRef.current.onPointerMove,
        );
      }
      if (dragStateRef.current.onPointerUp) {
        window.removeEventListener(
          'pointerup',
          dragStateRef.current.onPointerUp,
        );
      }

      const initialX = e.clientX;
      const initialY = e.clientY;
      const initialRect = rect;
      const anchorFrame = localFrame;
      const { width: visWidth, height: visHeight } = visibleBox;
      // 화면 px → 저장 좌표 fraction: 캔버스 줌과 애니 확대 표시를 모두 되돌린다
      const effectiveScale = scale * animScale;

      const writeRect = (next: ZoomRect, commitToUndoStack: boolean) => {
        setState({
          update: (s) =>
            changeItem(s, itemId, (i) => ({
              ...i,
              zoomKeyframes: upsertZoomKeyframe(getItemZoomKeyframes(i), {
                frame: anchorFrame,
                ...clampZoomRect(next),
              }),
            })),
          commitToUndoStack,
        });
      };

      const onPointerMove = (moveEvent: PointerEvent) => {
        const deltaX =
          (moveEvent.clientX - initialX) / effectiveScale / visWidth;
        const deltaY =
          (moveEvent.clientY - initialY) / effectiveScale / visHeight;

        const next =
          corner === 'move'
            ? {
                ...initialRect,
                left: initialRect.left + deltaX,
                top: initialRect.top + deltaY,
              }
            : resizeZoomRectFromCorner(initialRect, corner, deltaX, deltaY);

        writeRect(next, false);
      };

      const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        dragStateRef.current.onPointerMove = null;
        dragStateRef.current.onPointerUp = null;
        setState({ update: (s) => s, commitToUndoStack: true });
      };

      dragStateRef.current.onPointerMove = onPointerMove;
      dragStateRef.current.onPointerUp = onPointerUp;
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerup', onPointerUp, { once: true });
    },
    [
      itemId,
      visibleBox,
      isInRange,
      rect,
      localFrame,
      scale,
      animScale,
      setState,
    ],
  );

  if (hidden || !visibleBox || !isInRange) {
    return null;
  }

  // 이하 좌표는 전부 컨테이너(=보이는 창) 로컬
  const regionLeft = rect.left * visibleBox.width;
  const regionTop = rect.top * visibleBox.height;
  const regionWidth = rect.size * visibleBox.width;
  const regionHeight = rect.size * visibleBox.height;

  // 보이는 창 내부에서 줌 rect 밖만 어둡게 (VideoMaskOverlay 의 clip-path 패턴)
  const dimClipPath = `polygon(
    0px 0px,
    0px ${visibleBox.height}px,
    ${regionLeft}px ${visibleBox.height}px,
    ${regionLeft}px ${regionTop}px,
    ${regionLeft + regionWidth}px ${regionTop}px,
    ${regionLeft + regionWidth}px ${regionTop + regionHeight}px,
    ${regionLeft}px ${regionTop + regionHeight}px,
    ${regionLeft}px ${visibleBox.height}px,
    ${visibleBox.width}px ${visibleBox.height}px,
    ${visibleBox.width}px 0px
  )`;

  const handleSize = HANDLE_SIZE / scale;
  const accent =
    'var(--color-editor-starter-accent, #3b82f6)' as React.CSSProperties['color'];

  return (
    <AbsoluteFill style={{ pointerEvents: 'none', zIndex: 1002 }}>
      {/* 보이는 창 컨테이너 — 애니 표시 변형(이동·확대)을 통째로 따라간다 */}
      <div
        style={{
          position: 'absolute',
          left: visibleBox.left + animOffsetX,
          top: visibleBox.top + animOffsetY,
          width: visibleBox.width,
          height: visibleBox.height,
          transform: animScale !== 1 ? `scale(${animScale})` : undefined,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            clipPath: dimClipPath,
            pointerEvents: 'none',
          }}
        />

        {/* 줌 rect — 내부 드래그로 이동 */}
        <div
          onPointerDown={(e) => startDrag(e, 'move')}
          style={{
            position: 'absolute',
            left: regionLeft,
            top: regionTop,
            width: regionWidth,
            height: regionHeight,
            border: `${2 / scale}px solid ${accent}`,
            cursor: 'move',
            pointerEvents: 'auto',
          }}
        />

        {/* 코너 핸들 — 비율 고정 리사이즈 */}
        {CORNERS.map((corner) => (
          <div
            key={corner}
            onPointerDown={(e) => startDrag(e, corner)}
            style={{
              position: 'absolute',
              left:
                (corner.includes('left')
                  ? regionLeft
                  : regionLeft + regionWidth) -
                handleSize / 2,
              top:
                (corner.includes('top')
                  ? regionTop
                  : regionTop + regionHeight) -
                handleSize / 2,
              width: handleSize,
              height: handleSize,
              backgroundColor: 'white',
              border: `${1 / scale}px solid ${accent}`,
              cursor:
                corner === 'top-left' || corner === 'bottom-right'
                  ? 'nwse-resize'
                  : 'nesw-resize',
              pointerEvents: 'auto',
            }}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};
